<template>
  <div class="relative">
    <div
      v-html="messageWithHighlightedElements"
      ref="textareaCover"
      class="textarea-cover font-size-16"
    />

    <Mentionable
      filtering-disabled
      insert-space
      omit-key
      :items="suggestions"
      :keys="['@']"
      :limit="15"
      ref="mentionableComponent"
      @search="debounceSearch"
      @apply="applyMention"
    >
      <v-textarea
        v-model="message"
        v-bind="$attrs"
        :autofocus="false"
        :class="{ 'comment-container': expandTextArea }"
        :hide-details="!validateTextArea"
        :auto-grow="autoGrow"
        no-resize
        ref="textarea"
        :rules="[
          value =>
            messageIsValid || $t('validation.form.this_field_is_required'),
        ]"
        :rows="controlledRows"
        :placeholder="placeholder || $t('common.add_comment')"
        :label="showLabel ? $t('common.comment_one') : ''"
        @keyup="scrollTextareaCover"
        @scroll="scrollTextareaCover"
        @focus="isFocused = true"
      >
      </v-textarea>
      <template v-slot:no-result>
        <template v-if="isSearchingAccounts">
          <div style="width: 200px;">
            <v-skeleton-loader type="list-item-avatar" />
            <v-skeleton-loader type="list-item-avatar" />
          </div>
        </template>

        <a
          v-else
          :href="$t('mentions.no_results_help_center_article')"
          class="my-3 font-size-14"
          target="__blank"
          style="width: 100%;"
          >{{ $t('mentions.no_results_label') }}</a
        >
      </template>

      <template v-slot:item-@="{ item }">
        <div
          class="d-flex align-center cursor-pointer comment-account-selector"
        >
          <UserAvatar size="32px" fontSize="12px" :account="item" />
          <span class="ml-2 mb-0 body-2 blue-grey--text text--darken-2 ">
            {{
              item.value.length > MAX_NAME_CHARACTERS
                ? `${item.value.slice(0, MAX_NAME_CHARACTERS)}...`
                : item.value
            }}
          </span>
        </div>
      </template>
    </Mentionable>

    <div
      class="d-flex flex-column comment-actions-container"
      :class="validateTextArea ? 'show-details-space' : 'hide-details-space'"
      v-if="isFocused"
    >
      <v-divider class="mt-0 mb-2 mx-3" />
      <div class="d-flex justify-space-between align-center px-3">
        <div>
          <EmojiPicker @insertEmoji="insertEmoji" />
          <v-icon
            class="ml-4"
            ref="atSignIcon"
            size="24"
            color="blue-grey"
            @click="handleAtSignClick"
            >mdi-at</v-icon
          >
        </div>

        <div v-if="!hasCustomFooter" class="d-flex justify-end">
          <slot name="additional-actions" />
          <Button
            color="primary"
            :disabled="disableButton"
            :data-testid="submitButtonTestId"
            :text="submitButtonText || $t('common.button.send')"
            @click="submit"
          />
        </div>
        <slot name="footer" :triggerSubmit="submit" :triggerCancel="cancel" />
      </div>
    </div>
  </div>
</template>

<script>
import { Mentionable } from 'vue-mention'
import _ from 'lodash'

import userMixin from '@/mixins/userMixin.vue'

import Button from '@/components/UI/Button.vue'
import EmojiPicker from '@/components/EmojiPicker.vue'
import UserAvatar from '@/components/UserAvatar.vue'

const MENTION_MARKUP_REGEX = /<span[^>]*>[^>]*<\/span>/gm
const ACCOUNT_ID_REGEX = /[0-9a-fA-F]{24}/
const MENTION_ID_REGEX = /@[0-9a-fA-F]{24}/g
const TAG_INNER_TEXT = /(>)(.*?)(<\/)/
const MAX_NAME_CHARACTERS = 100
const INITIAL_ROWS = 3

export default {
  name: 'MessageComposer',

  mixins: [userMixin],

  components: {
    Mentionable,
    Button,
    EmojiPicker,
    UserAvatar,
  },

  props: {
    rows: {
      type: [Number, String],
      default: 1,
    },

    accountSuggestions: {
      type: Array,
      default: () => [],
    },

    hasCustomFooter: {
      type: Boolean,
      default: false,
    },

    initialMessage: {
      type: String,
      default: '',
    },

    initialMentionedAccounts: {
      type: Array,
      default: () => [],
    },

    isSearchingAccounts: {
      type: Boolean,
      default: false,
    },

    placeholder: String,

    submitButtonTestId: {
      type: String,
      default: 'button-submitMessage',
    },

    submitButtonText: String,

    textareaTestId: {
      type: String,
      default: 'textarea-message',
    },

    validator: {
      type: [Function, Boolean],
      required: false,
    },

    disableSubmitButton: {
      type: Boolean,
      default: false,
    },

    showLabel: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      MAX_NAME_CHARACTERS,
      message: '',
      mentionedAccounts: this.initialMentionedAccounts.map(
        this.parseMentionedAccount
      ),
      suggestions: [],
      isFocused: false,
      autoGrow: false,
    }
  },

  watch: {
    accountSuggestions(accounts) {
      this.suggestions = accounts.map(account => {
        return {
          ...account,
          value: this.getFullName(account),
        }
      })
    },

    initialMentionedAccounts(accounts) {
      this.mentionedAccounts = accounts.map(this.parseMentionedAccount)
      this.$refs?.textarea?.$refs?.input?.focus()
      this.setComputedStyle()
    },

    initialMessage(message) {
      this.message = this.getMessageWithMentionsAsNames(message)
      this.$refs.textarea.$refs.input.focus()
      this.setComputedStyle()
    },
  },

  async mounted() {
    await this.$nextTick()
    this.setComputedStyle()
  },

  computed: {
    messageWithHighlightedElements() {
      const sanitizedMessage =
        this.message
          .replace(/&/g, '&amp;')
          .replace(/>/g, '&gt;')
          .replace(/</g, '&lt;') + '\n\n'

      if (_.isEmpty(this.mentionedAccounts)) return sanitizedMessage // We ensure the sanitization process is run to prevent XSS injections

      const search = new RegExp(
        this.mentionedAccounts
          .slice()
          .sort((a, b) => b.fullName?.length - a.fullName?.length)
          .map(account => account.fullName)
          .map(account => account.replace('(', '\\(').replace(')', '\\)')) // we have to escape the parenthesis characters so they can be properly replaced when generating the span tags
          .join('|'),
        'g'
      )

      const mutableMentionededAccounts = [...this.mentionedAccounts]

      return sanitizedMessage.replace(search, fullNameMatch => {
        const matchIndex = mutableMentionededAccounts.findIndex(
          account => account.fullName === fullNameMatch
        )

        const accountMatch = mutableMentionededAccounts[matchIndex]
        mutableMentionededAccounts.splice(matchIndex, 1)

        return `<span class="mention-str" data-account-id="${accountMatch?.id}">${fullNameMatch}</span>`
      })
    },

    messageIsValid() {
      if (!this.validateTextArea) return true
      return this.validateTextArea && !!this.message.trim()
    },

    disableIfEmptyMessage() {
      return Boolean(!this.message.trim())
    },

    disableButton() {
      return this.disableSubmitButton || this.disableIfEmptyMessage
    },

    expandTextArea() {
      return this.isFocused || this.message.trim()
    },

    controlledRows() {
      return this.expandTextArea ? INITIAL_ROWS : this.rows
    },

    validateTextArea() {
      return Boolean(this.validator)
    },
  },

  methods: {
    debounceSearch: _.debounce(function(searchText) {
      this.$emit('searchAccounts', searchText)
    }, 500),

    async applyMention(account) {
      this.mentionedAccounts.push(this.parseMentionedAccount(account))
      await this.$nextTick()
      this.setComputedStyle()
      setTimeout(() => this.$refs.textarea.$refs.input.focus(), 500)
    },
    setComputedStyle() {
      const inputComputedStyle = getComputedStyle(
        this.$refs?.textarea?.$refs?.input
      )

      if (this?.$refs?.textareaCover) {
        this.$refs.textareaCover.style.marginTop = inputComputedStyle.marginTop

        this.$refs.textareaCover.style.paddingTop =
          inputComputedStyle.paddingTop

        this.$refs.textareaCover.style.paddingBottom =
          inputComputedStyle.paddingBottom

        this.$refs.textareaCover.style.paddingRight = '14px'

        this.$refs.textareaCover.style.letterSpacing =
          inputComputedStyle.letterSpacing

        this.$refs.textareaCover.style.lineHeight =
          inputComputedStyle.lineHeight

        this.$refs.textareaCover.style.height = inputComputedStyle.height

        this.$refs.textareaCover.style.maxHeight = inputComputedStyle.maxHeight

        this.$refs.textareaCover.style.width =
          inputComputedStyle.width + inputComputedStyle.paddingRight

        this.autoGrow = this.rowsCount() > this.controlledRows
      }
    },
    scrollTextareaCover() {
      this.$refs.textareaCover.scrollTop = this.$refs.textarea.$refs.input.scrollTop
      this.setComputedStyle()
    },

    async insertEmoji(emoji) {
      const textarea = this.$refs.textarea.$refs.input
      const carretPosition = textarea.selectionStart
      const newCarretPosition = carretPosition + emoji.length

      this.message =
        this.message.slice(0, carretPosition) +
        emoji +
        this.message.slice(carretPosition)

      await this.$nextTick()

      textarea.setSelectionRange(newCarretPosition, newCarretPosition)
      textarea.focus()
      this.$track('emoji.Select', this.$store, { emoji: emoji })
    },

    submit() {
      const parsedMessage = this.getMessageWithMentionsAsIds()
      const accountIds = this.getAccountIdsFromParsedMessage(parsedMessage)
      const payload = {
        message: parsedMessage,
        accountIds: accountIds,
      }

      if (this.validator && !this.validator(payload)) return

      this.$emit('submit', payload)
      this.message = ''
      this.mentionedAccounts = []
    },

    cancel() {
      if (this.validateTextArea) {
        this.$refs.textarea.resetValidation()
        this.isFocused = false
      }
      this.$emit('cancel')
      this.message = ''
      this.mentionedAccounts = []
    },

    getMessageWithMentionsAsIds() {
      const rawHTML = this.$refs.textareaCover?.innerHTML?.trim() ?? ''

      return rawHTML.replace(MENTION_MARKUP_REGEX, match => {
        const accountId = match.match(ACCOUNT_ID_REGEX)?.[0] ?? ''
        const innerText = match.match(TAG_INNER_TEXT)?.[2] ?? '' // Safari doesn't support regexes using lookbehind/lookahead, so we have to look for the specific group we need
        return accountId ? `@${accountId}` : innerText
      })
    },

    getAccountIdsFromParsedMessage(parsedMessage) {
      const mentionIds = parsedMessage.match(MENTION_ID_REGEX) ?? []
      return mentionIds.map(accountId => accountId.replace('@', ''))
    },

    parseMentionedAccount(account) {
      return {
        id: account.id,
        fullName: this.getFullName(account),
      }
    },

    getMessageWithMentionsAsNames(message) {
      const accountsMap = _.keyBy(this.mentionedAccounts, 'id')

      return message.replace(MENTION_ID_REGEX, match => {
        const accountId = match.split('@')[1]
        const account = accountsMap[accountId]
        return account ? account.fullName : match
      })
    },

    handleAtSignClick() {
      const textarea = this.$refs.textarea.$refs.input
      textarea.focus()
      this.$refs.mentionableComponent.setValue(`${this.message}${'@'}`)
    },

    rowsCount() {
      const text = this.$refs?.textarea?.value
      return text ? text.split(/\r|\r\n|\n/).length : 0
    },
  },
}
</script>
<style lang="scss">
[id^='popover_'].tooltip {
  font-family: $body-font-family;
  display: block !important;
  z-index: 10000;
  border: 1px solid $color-blue-grey-lighten2;

  .tooltip-inner {
    background: black;
    color: white;
    border-radius: 16px;
    padding: 5px 10px 4px;
  }

  .tooltip-arrow {
    width: 0;
    height: 0;
    border-style: solid;
    position: absolute;
    margin: 5px;
    border-color: black;
    z-index: 1;
    border: 1px solid $color-blue-grey-lighten2;
  }

  &[x-placement^='top'] {
    margin-bottom: 5px;

    .tooltip-arrow {
      border-width: 5px 5px 0 5px;
      border-left-color: transparent !important;
      border-right-color: transparent !important;
      border-bottom-color: transparent !important;
      bottom: -5px;
      left: calc(50% - 5px);
      margin-top: 0;
      margin-bottom: 0;
    }
  }

  &[x-placement^='bottom'] {
    margin-top: 5px;

    .tooltip-arrow {
      border-width: 0 5px 5px 5px;
      border-left-color: transparent !important;
      border-right-color: transparent !important;
      border-top-color: transparent !important;
      top: -5px;
      left: calc(50% - 5px);
      margin-top: 0;
      margin-bottom: 0;
    }
  }

  &[x-placement^='right'] {
    margin-left: 5px;

    .tooltip-arrow {
      border-width: 5px 5px 5px 0;
      border-left-color: transparent !important;
      border-top-color: transparent !important;
      border-bottom-color: transparent !important;
      left: -5px;
      top: calc(50% - 5px);
      margin-left: 0;
      margin-right: 0;
    }
  }

  &[x-placement^='left'] {
    margin-right: 5px;

    .tooltip-arrow {
      border-width: 5px 0 5px 5px;
      border-top-color: transparent !important;
      border-right-color: transparent !important;
      border-bottom-color: transparent !important;
      right: -5px;
      top: calc(50% - 5px);
      margin-left: 0;
      margin-right: 0;
    }
  }

  &.popover {
    $color: $color-white;

    .popover-inner {
      background: $color;
      color: black;
      padding: 10px;
      border-radius: 5px;
      box-shadow: 0 5px 30px rgba(black, 0.1);
    }

    .popover-arrow {
      border-color: $color;
    }
  }

  &[aria-hidden='true'] {
    visibility: hidden;
    opacity: 0;
    transition: opacity 0.15s, visibility 0.15s;
  }

  &[aria-hidden='false'] {
    visibility: visible;
    opacity: 1;
    transition: opacity 0.15s;
  }
}

.mention-item {
  padding: 4px 10px;
}

.mention-selected {
  background-color: $color-blue-grey-lighten2;
}

.textarea-cover {
  z-index: 1;
  position: absolute;
  color: transparent;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  overflow-y: hidden;
  padding-left: 12px;
  border: 1px solid transparent;
}

.comment-container {
  .v-text-field__slot {
    padding-bottom: 58px;
    padding-right: 3px;
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
    ::-webkit-scrollbar {
      display: none;
    }
  }

  textarea {
    max-height: 9rem !important;
    overflow: auto;
  }
}

.comment {
  width: 100%;
}

.mention-str {
  color: $color-primary;
  background-color: $color-blue-lighten2;
}

.comment-actions-container {
  position: absolute;
  bottom: 0;
  width: 100%;
}

.hide-details-space {
  margin-bottom: 10px;
}

.show-details-space {
  margin-bottom: 35px;
}
</style>
